/**
 * postTemplateColumns — shared column definitions for the SDataTable that
 * displays `PostTemplate` rows. Single source of truth for the row layout
 * driving:
 *   - SystemTemplateBrowserDialog (system catalogue + per-author pick mode)
 *   - TopicTemplatesTab (selected templates list per platform)
 *   - any future surface that lists post templates
 *
 * Columns are stable (Name / Goal / Scaffold / Tag). Optional caller-
 * provided sets toggle two name-cell badges:
 *   - `savedSelectedSet`   — renders a primary "Selected" chip
 *   - `importedTemplateIds`— renders a success "Imported" chip
 *
 * Pass neither for plain rows.
 */

import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { SDataTableColumnDef } from '@modules/ui-components';
import type { PostTemplate } from '@segmently/schema-models/content-plan';

export interface BuildPostTemplateColumnsOptions {
  /** Show "Selected" chip on rows whose id is in this set. */
  savedSelectedSet?: Set<string>;
  /** Show "Imported" chip on rows whose id is in this set. */
  importedTemplateIds?: Set<string>;
}

export function buildPostTemplateColumns(
  opts: BuildPostTemplateColumnsOptions = {},
): SDataTableColumnDef<PostTemplate>[] {
  const { savedSelectedSet, importedTemplateIds } = opts;
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 220,
      Cell: ({ row }) => {
        const t = row.original;
        const isSelected = savedSelectedSet?.has(t.id) ?? false;
        const isImported = importedTemplateIds?.has(t.id) ?? false;
        return (
          <Stack spacing={0.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={500}>
                {t.name || t.id}
              </Typography>
              {isSelected && (
                <Chip label="Selected" size="small" color="primary" />
              )}
              {isImported && (
                <Chip label="Imported" size="small" color="success" variant="outlined" />
              )}
            </Stack>
            {t.description && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {t.description}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      accessorKey: 'goal',
      header: 'Goal',
      size: 110,
      Cell: ({ cell }) => (
        <Chip label={cell.getValue<string>() ?? '—'} size="small" color="primary" />
      ),
    },
    {
      id: 'formats',
      header: 'Formats',
      size: 170,
      enableSorting: false,
      Cell: ({ row }) => {
        const types = Array.isArray((row.original as any).compatibleFormatTypes)
          ? (row.original as any).compatibleFormatTypes.slice(0, 4)
          : [];
        return types.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {types.map((type: string) => (
              <Chip key={type} label={type} size="small" variant="outlined" />
            ))}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">Any</Typography>
        );
      },
    },
    {
      accessorKey: 'scaffold',
      header: 'Scaffold',
      size: 480,
      enableSorting: false,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>() || '';
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              color: 'text.secondary',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
              maxWidth: 460,
            }}
            title={v}
          >
            {v}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'tag',
      header: 'Tag',
      size: 110,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>();
        return v ? (
          <Typography variant="caption" color="text.secondary">
            {v}
          </Typography>
        ) : null;
      },
    },
  ];
}

/**
 * Convenience: append an "actions" column to a column list. Use for
 * surfaces where each row needs a per-row affordance (e.g. remove from
 * selection in TopicTemplatesTab). Caller controls the icon + handler.
 */
export function withActionsColumn(
  columns: SDataTableColumnDef<PostTemplate>[],
  renderAction: (template: PostTemplate) => React.ReactNode,
  size: number = 60,
): SDataTableColumnDef<PostTemplate>[] {
  return [
    ...columns,
    {
      id: 'actions',
      header: '',
      size,
      enableSorting: false,
      Cell: ({ row }) => <Box>{renderAction(row.original)}</Box>,
    },
  ];
}
